import { supabase } from '../lib/supabase';

export type JobType = 'transcode_master' | 'thumbnail' | 'clip' | 'stitch_clips' | 'video_overlay_enhancements' | 'social_media_upload' | 'remote_video_upload';
export type JobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface VideoProcessingJob {
  id: string;
  video_id: string | null;
  highlight_id: string | null; // For stitch_clips jobs linked to highlights
  job_type: JobType;
  payload: Record<string, any>;
  status: JobStatus;
  depends_on: string | null; // References another job that must complete before this job can run
  attempts: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface CreateJobInput {
  video_id: string | null;
  highlight_id?: string | null;
  job_type: JobType;
  payload: Record<string, any>;
}

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Service for managing video processing jobs
 */
export const videoProcessingJobsService = {
  /**
   * Get all jobs for a video
   */
  getJobsByHighlightId: async (highlightId: string): Promise<ServiceResult<VideoProcessingJob[]>> => {
    try {
      const { data, error } = await supabase
        .from('video_processing_jobs')
        .select('*')
        .eq('highlight_id', highlightId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching jobs by highlight ID:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in getJobsByHighlightId:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  getJobsByVideoId: async (videoId: string): Promise<ServiceResult<VideoProcessingJob[]>> => {
    try {
      const { data, error } = await supabase
        .from('video_processing_jobs')
        .select('*')
        .eq('video_id', videoId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching jobs:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in getJobsByVideoId:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Get all queued jobs (for worker polling)
   */
  getQueuedJobs: async (limit: number = 10): Promise<ServiceResult<VideoProcessingJob[]>> => {
    try {
      const { data, error } = await supabase
        .from('video_processing_jobs')
        .select('*')
        .eq('status', 'queued')
        .eq('is_active', true)
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) {
        console.error('Error fetching queued jobs:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in getQueuedJobs:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Create a new processing job
   */
  createJob: async (input: CreateJobInput): Promise<ServiceResult<VideoProcessingJob>> => {
    try {
      const { data, error } = await supabase
        .from('video_processing_jobs')
        .insert({
          video_id: input.video_id || null,
          highlight_id: input.highlight_id || null,
          job_type: input.job_type,
          payload: input.payload,
          status: 'queued',
          attempts: 0,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating job:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in createJob:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Create multiple jobs at once (batch)
   */
  createJobs: async (inputs: CreateJobInput[]): Promise<ServiceResult<VideoProcessingJob[]>> => {
    try {
      const jobs = inputs.map((input) => ({
        video_id: input.video_id || null,
        highlight_id: input.highlight_id || null,
        job_type: input.job_type,
        payload: input.payload,
        status: 'queued' as JobStatus,
        attempts: 0,
      }));

      const { data, error } = await supabase
        .from('video_processing_jobs')
        .insert(jobs)
        .select();

      if (error) {
        console.error('Error creating jobs:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in createJobs:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Update job status
   */
  updateJobStatus: async (
    jobId: string,
    status: JobStatus,
    errorMessage?: string
  ): Promise<ServiceResult<VideoProcessingJob>> => {
    try {
      const updates: Partial<VideoProcessingJob> = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (errorMessage) {
        updates.error_message = errorMessage;
      }

      if (status === 'running') {
        const { data: existingJob, error: fetchError } = await supabase
          .from('video_processing_jobs')
          .select('attempts')
          .eq('id', jobId)
          .single();

        if (fetchError) {
          console.error('Error fetching job attempts:', fetchError);
        } else {
          const nextAttempts = (existingJob?.attempts ?? 0) + 1;
          updates.attempts = nextAttempts;
        }
      }

      const { data, error } = await supabase
        .from('video_processing_jobs')
        .update(updates)
        .eq('id', jobId)
        .select()
        .single();

      if (error) {
        console.error('Error updating job:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in updateJobStatus:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Retry a failed job
   * Uses edge function to bypass RLS policies and handle job retry properly
   */
  retryJob: async (jobId: string): Promise<ServiceResult<VideoProcessingJob>> => {
    try {
      // Get Supabase URL and auth token
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        return { success: false, error: 'Supabase URL not configured' };
      }
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/retry-job`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ jobId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        return { success: false, error: errorData.error || `HTTP ${response.status}` };
      }

      // Edge function succeeded - fetch the updated job to return complete data
      const { data: updatedJob, error: fetchError } = await supabase
        .from('video_processing_jobs')
        .select('*')
        .eq('id', jobId)
        .single();

      if (fetchError || !updatedJob) {
        // Edge function succeeded but we can't fetch the job (RLS might block)
        // Return success anyway since the edge function confirmed the update
        console.warn('Retry succeeded but could not fetch updated job:', fetchError);
        return { success: true };
      }

      return { success: true, data: updatedJob };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('retryJob failed:', message);
      return { success: false, error: message };
    }
  },

  /**
   * Get thumbnail jobs for multiple video IDs (batch query for performance)
   * Uses a join through videos table to respect RLS policies
   */
  getThumbnailJobsByVideoIds: async (videoIds: string[]): Promise<ServiceResult<VideoProcessingJob[]>> => {
    if (!videoIds.length) {
      return { success: true, data: [] };
    }
    
    try {
      // Query through videos table to respect RLS, then get related processing jobs
      const { data, error } = await supabase
        .from('videos')
        .select(`
          id,
          video_processing_jobs!inner (
            id,
            video_id,
            job_type,
            payload,
            status,
            attempts,
            error_message,
            created_at,
            updated_at,
            is_active
          )
        `)
        .in('id', videoIds)
        .eq('video_processing_jobs.job_type', 'thumbnail')
        .eq('video_processing_jobs.status', 'succeeded')
        .eq('video_processing_jobs.is_active', true);

      if (error) {
        console.error('Error fetching thumbnail jobs:', error);
        return { success: false, error: error.message };
      }

      // Flatten the nested structure
      const jobs: VideoProcessingJob[] = [];
      if (data) {
        data.forEach((video: any) => {
          if (video.video_processing_jobs) {
            const jobsArray = Array.isArray(video.video_processing_jobs) 
              ? video.video_processing_jobs 
              : [video.video_processing_jobs];
            jobs.push(...jobsArray);
          }
        });
      }

      return { success: true, data: jobs };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in getThumbnailJobsByVideoIds:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Delete a job (soft delete by setting is_active to false)
   */
  deleteJob: async (jobId: string): Promise<ServiceResult<void>> => {
    try {
      const { error } = await supabase
        .from('video_processing_jobs')
        .update({
          is_active: false,
          updated_at: new Date().toISOString(),
        })
        .eq('id', jobId);

      if (error) {
        console.error('Error deleting job:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in deleteJob:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Process a video (create transcode_master job)
   * Uses edge function to create the processing job after upload completes
   * @param videoId - Video ID to process
   * @param matchId - Optional match ID to link video to match after transcode completes
   */
  processVideo: async (videoId: string, matchId?: string): Promise<ServiceResult<{ jobId: string; videoId: string; status: string }>> => {
    try {
      // Get Supabase URL and auth token
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        return { success: false, error: 'Supabase URL not configured' };
      }
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/process-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ videoId, matchId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        return { success: false, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('processVideo failed:', message);
      return { success: false, error: message };
    }
  },
};

